import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HeadersService } from '../headers/headers.service';
import { ApiService } from '../api.service';
import { firstValueFrom } from 'rxjs';
import { UserSessionStore } from '../../user-session.store';
import { SKIP_AUTH_REDIRECT } from '../../../interceptors/auth.interceptor';
import { UserSession } from 'src/app/core/user/user.model';

export interface Discovery {
  sso: boolean;
  authorization_url?: string;
  slug?: string;
  social_providers?: ('google' | 'microsoft')[];
  has_password?: boolean;
}

export interface AuthStatus {
  connected: boolean;
}

export interface SocialSigninResponse {
  authorization_url: string;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  constructor(
    private http: HttpClient,
    private headersService: HeadersService,
    private apiService: ApiService,
    private sessionStore: UserSessionStore
  ) {}

  async login(email: string, password: string, captcha: string): Promise<void> {
    const sanitizedEmail = email.trim().toLowerCase();
    const session = await firstValueFrom(
      this.apiService.account.getUserSession({
        email: sanitizedEmail,
        password,
        captcha_token: captcha,
      })
    );
    this.sessionStore.setSession(session);
  }

  async checkCurrentPassword(password: string): Promise<void> {
    const email = this.sessionStore.userSession()?.email ?? '';
    const userSession = await firstValueFrom(this.apiService.account.getUserSession({ email, password }));
    this.sessionStore.updateApiToken(userSession.apiToken);
  }

  async authenticateWithCookie(): Promise<UserSession> {
    const session = await firstValueFrom(this.apiService.account.getUserSession());
    this.sessionStore.setSession(session);
    return session;
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.explainApiUrl}auth/sign_out`,
          {},
          { headers: { [SKIP_AUTH_REDIRECT]: 'true' }, withCredentials: true }
        )
      );
    } catch (error) {
      console.error('Error logging out:', error);
    }
    this.sessionStore.clearSession();
  }

  forgotPassword(email: string) {
    const sanitizedEmail = email.trim().toLowerCase();
    return this.http.post(`${environment.explainApiUrl}account/password/request_reset`, { email: sanitizedEmail });
  }

  resetPassword(newPassword: string, resetPasswordToken: string) {
    return this.http.post(`${environment.explainApiUrl}account/password/reset`, {
      new_password: newPassword,
      reset_password_token: resetPasswordToken,
    });
  }

  updatePassword(newPassword: string, password: string) {
    return this.http.post(
      `${environment.explainApiUrl}account/password/update`,
      { new_password: newPassword, password },
      this.headersService.httpHeaders
    );
  }

  checkResetPasswordToken(token: string | null) {
    return this.http.post(`${environment.explainApiUrl}account/password/reset`, { reset_password_token: token });
  }

  getAuthStatus(service: string): Observable<AuthStatus> {
    return this.http.get<AuthStatus>(`${environment.explainApiUrl}auth/status?service=${service}`, this.headersService.httpHeaders);
  }

  verifyAddinCreation(uid: string) {
    // Skip auth redirect to avoid redirect to login page
    const headers = this.headersService.httpHeaders.headers.set(SKIP_AUTH_REDIRECT, 'true');
    return this.http.post(`${environment.explainApiUrl}auth/create_verification`, { uid }, { headers });
  }

  discover(params: { email?: string; slug?: string; redirect_url?: string }) {
    return firstValueFrom(this.http.post<Discovery>(`${environment.explainApiUrl}auth/sso/discover`, params));
  }

  socialSignin(provider: 'google' | 'microsoft', redirect_url?: string) {
    return firstValueFrom(
      this.http.post<SocialSigninResponse>(`${environment.explainApiUrl}auth/social/signin`, { provider, redirect_url })
    );
  }
}
